import { number, object, string, bool } from "yup";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { getDataGeneric } from "../../../repository/DataGeneric";
import { RequestHelper } from "../../../utils/requestHelper";
import { _ROUTES } from "../../../utils/_routes_constants";
import { createToast } from "../../../utils/toast";
import {
  _HTTP,
  _SWAL_ICONS,
  _MESSAGES,
  _ACTIONS,
} from "../../../utils/constants";
import {
  currentFiscalYearDateSelect,
  getOptionsSelectFiscalYear,
} from "../../../utils/function";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import { Box, Button } from "@mui/material";

const Parameters = () => {
  const [budgetParameters, setBudgetParameters] = useState([]);
  const [options, setOptions] = useState({
    currentFiscalYearDate: [],
    forecastFiscalYearDate: [],
    dataEntryFiscalYearDate: [],
  });
  const requestHelper = new RequestHelper();
  const currentFiscalYear = currentFiscalYearDateSelect();

  useEffect(() => {
    if (budgetParameters.length === 0) {
      getDataGeneric(
        requestHelper,
        {},
        setBudgetParameters,
        _ROUTES.BUDGET_PARAMETERS,
        "Unable to retrieve budget parameters",
      );
    }

    const yearsOptions = getOptionsSelectFiscalYear();
    setOptions({
      currentFiscalYearDate: yearsOptions.map((y) => ({ value: y, label: y })),
      forecastFiscalYearDate: yearsOptions.map((y) => ({ value: y, label: y })),
      dataEntryFiscalYearDate: yearsOptions.map((y) => ({
        value: y,
        label: y,
      })),
    });
  }, []);

  const v = object({
    currentFiscalYearDate: string().required().label("Current Budget"),
    currentFiscalYearVersion: number()
      .required()
      .label("Current budget version"),
    forecastFiscalYearVersion: number().required().label("Forecast version"),
    forecastFiscalYearDate: string().required().label("Forecast Budget"),
    dataEntryFiscalYearDate: string().required().label("Entry budget"),
    dataEntryFiscalYearVersion: number()
      .required()
      .label("Entry budget version"),
    numberOfPreviousFiscalYearsDisplayed: number()
      .required()
      .min(1)
      .max(20)
      .label("Number of previous fiscal years displayed"),
    dataEntryEnableHr: bool().required().label("Data entry HR enable"),
    dataEntryEnableSales: bool().required().label("Data entry Sales enable"),
    dataEntryEnablePayroll: bool()
      .required()
      .label("Data entry Payroll enable"),
    dataEntryEnableDepreciations: bool()
      .required()
      .label("Data entry Deprecations enable"),
    dataEntryEnableIt: bool().required().label("Data entry IT enable"),
    dataEntryEnableEngineering: bool()
      .required()
      .label("Data entry Engineering enable"),
    dataEntryEnableFleetOp: bool()
      .required()
      .label("Data entry Fleet OP enable"),
    dataEntryEnableHealthSafety: bool()
      .required()
      .label("Data entry Health and Safety enable"),
    dataEntryEnableLru: bool().required().label("Data entry enable LRU"),
  });

  const formik = useFormik({
    initialValues: {
      currentFiscalYearDate: currentFiscalYear,
      currentFiscalYearVersion: 1,
      forecastFiscalYearDate: currentFiscalYear,
      forecastFiscalYearVersion: 1,
      dataEntryFiscalYearDate: currentFiscalYear,
      dataEntryFiscalYearVersion: 1,
      numberOfPreviousFiscalYearsDisplayed: "3",
      dataEntryEnableHr: false,
      dataEntryEnableSales: false,
      dataEntryEnablePayroll: false,
      dataEntryEnableDepreciations: false,
      dataEntryEnableIt: false,
      dataEntryEnableEngineering: false,
      dataEntryEnableFleetOp: false,
      dataEntryEnableHealthSafety: false,
      dataEntryEnableLru: false,
    },
    validationSchema: v,
    onSubmit: (values) => {
      const methode =
        budgetParameters.length > 0 ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url =
        _ROUTES.BUDGET_PARAMETERS +
        (budgetParameters.length > 0 ? `/${budgetParameters[0].id}` : "");

      requestHelper
        .doRequest(methode, url, {
          ...values,
          numberOfPreviousFiscalYearsDisplayed: parseInt(
            values.numberOfPreviousFiscalYearsDisplayed,
          ),
        })
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            createToast(
              _SWAL_ICONS.success,
              "Budget parameters saved successfully",
            );
          } else {
            throw new Error("Error saving budget parameters");
          }
        })
        .catch((err) => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
  });

  formik["special"] = {
    customStructure: {
      0: ["forecastFiscalYearDate", "forecastFiscalYearVersion"],
      1: ["currentFiscalYearDate", "currentFiscalYearVersion"],
      2: ["dataEntryFiscalYearDate", "dataEntryFiscalYearVersion"],
      2.5: ["numberOfPreviousFiscalYearsDisplayed"],
      3: ["dataEntryEnableHr", "dataEntryEnableSales"],
      4: ["dataEntryEnablePayroll", "dataEntryEnableDepreciations"],
      5: ["dataEntryEnableIt", "dataEntryEnableEngineering"],
      6: ["dataEntryEnableFleetOp", "dataEntryEnableHealthSafety"],
      7: ["dataEntryEnableLru"],
    },
    layout: {
      currentFiscalYearDate: {
        selectWidth: "13rem",
        enableLeftLabel: true,
      },
      currentFiscalYearVersion: {
        enableLeftLabel: true,
      },
      numberOfPreviousFiscalYearsDisplayed: {
        enableLeftLabel: true,
      },
      forecastFiscalYearDate: {
        selectWidth: "13rem",
        enableLeftLabel: true,
      },
      forecastFiscalYearVersion: {
        enableLeftLabel: true,
      },
      dataEntryFiscalYearDate: {
        selectWidth: "13rem",
        enableLeftLabel: true,
      },
      dataEntryFiscalYearVersion: {
        enableLeftLabel: true,
      },
    },
  };

  useEffect(() => {
    if (budgetParameters.length > 0) {
      formik.setValues({
        currentFiscalYearDate: budgetParameters[0].currentFiscalYearDate,
        currentFiscalYearVersion: budgetParameters[0].currentFiscalYearVersion,
        forecastFiscalYearDate: budgetParameters[0].forecastFiscalYearDate,
        forecastFiscalYearVersion:
          budgetParameters[0].forecastFiscalYearVersion,
        dataEntryFiscalYearDate: budgetParameters[0].dataEntryFiscalYearDate,
        dataEntryFiscalYearVersion:
          budgetParameters[0].dataEntryFiscalYearVersion,
        numberOfPreviousFiscalYearsDisplayed:
          budgetParameters[0].numberOfPreviousFiscalYearsDisplayed,
        dataEntryEnableHr: budgetParameters[0].dataEntryEnableHr,
        dataEntryEnableSales: budgetParameters[0].dataEntryEnableSales,
        dataEntryEnablePayroll: budgetParameters[0].dataEntryEnablePayroll,
        dataEntryEnableDepreciations:
          budgetParameters[0].dataEntryEnableDepreciations,
        dataEntryEnableIt: budgetParameters[0].dataEntryEnableIt,
        dataEntryEnableEngineering:
          budgetParameters[0].dataEntryEnableEngineering,
        dataEntryEnableFleetOp: budgetParameters[0].dataEntryEnableFleetOp,
        dataEntryEnableHealthSafety:
          budgetParameters[0].dataEntryEnableHealthSafety,
        dataEntryEnableLru: budgetParameters[0].dataEntryEnableLru,
      });
    }
  }, [budgetParameters]);

  return (
    <>
      <BasicFormikForm formik={formik} validations={v} options={options} />
      <Box sx={{ display: "flex", justifyContent: "space-around" }}>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          size="small"
          color="success"
        >
          Save
        </Button>
      </Box>
    </>
  );
};

export default Parameters;
